import _max from "lodash/max";
import _min from "lodash/min";

import { FeatureImportanceSummary } from "../types";
import { EChartsTransformer } from "./transformer";
import { formatAxisLabel, stripCodeSuffix } from "./utils/labelFormatter";

export const shapSummaryTransformer: EChartsTransformer<FeatureImportanceSummary> = {
  transform: (dpData, wellData, chartContext, __, target) => {
    const isFullscreen = chartContext?.isFullscreen ?? false;
    const targetUnitLabel = target?.Unit ? ` (${target.Unit})` : "";

    if (!dpData) {
      return {};
    }
    function beeswarmOffsetsByX(
      values: number[],
      symbolSize: number,
      binSize: number,
      rowHeight: number
    ) {
      const bins = {};
      values.forEach((v, i) => {
        const bin = Math.round(v / binSize) * binSize;
        if (!bins[bin]) bins[bin] = [];
        bins[bin].push(i);
      });

      const offsets = new Array(values.length).fill(0);
      const step = Math.min(symbolSize - 1, rowHeight / 10);
      const maxOffset = Math.floor(rowHeight / 2 - symbolSize / 2);

      // For each bin, place first at center, then alternate up/down by n px, but clamp to maxOffset
      Object.values(bins).forEach((indices: number[]) => {
        for (let k = 0; k < indices.length; k++) {
          let offset = 0;
          if (k > 0) {
            const direction = k % 2 === 0 ? 1 : -1;
            const magnitude = Math.ceil(k / 2);
            offset = direction * magnitude * step;
            // Clamp to maxOffset
            if (offset > maxOffset) offset = maxOffset;
            if (offset < -maxOffset) offset = -maxOffset;
          }
          offsets[indices[k]] = offset;
        }
      });

      return offsets;
    }
    const featureRankEntity = dpData.feature_rank_entity;
    const entities = featureRankEntity.entities;
    const featureData = featureRankEntity.data;

    const series = [];
    const categoryData = [
      ...new Set(featureData.map((fd) => stripCodeSuffix(fd.NiceName)))
    ]; // Unique feature names for y-axis (without _CODE suffix)

    // Helper function to get color based on value
    const getColorForValue = (value, min, max) => {
      // Normalize value between 0 and 1
      const normalized = (value - min) / (max - min);

      // Interpolate between blue and red without grey tones
      const r = Math.round(normalized * 255);
      const b = Math.round((1 - normalized) * 255);
      const g = 0; // Keep green at 0 to avoid muddy/grey colors

      return `rgb(${r},${g},${b})`;
    };
    const numCategories = categoryData.length;
    const chartHeight = 160;
    const rowHeight = chartHeight / numCategories;
    for (let j = 0; j < featureData.length; j++) {
      const featureName = stripCodeSuffix(featureData[j].NiceName);
      const shapValues = featureData[j].Data; // SHAP values for this feature
      const featureValues = featureData[j].FeatureValues; // Actual values of the feature
      const scaleMin = _min(featureValues);
      const scaleMax = _max(featureValues);
      // Dynamically calculate rowHeight based on number of categories and chart height (240px)

      const yOffsets = beeswarmOffsetsByX(shapValues, 6, 0.2, rowHeight);

      // Prepare data for scatter plot: [x, y, entityName, group, featureValue]
      const data = shapValues.map((shapVal, idx) => {
        const entity = entities[idx];
        const group = wellData?.[entity]?.title || "N/A";
        return {
          value: [shapVal, featureName, entity, group, featureValues[idx]],
          itemStyle: {
            color: getColorForValue(featureValues[idx], scaleMin, scaleMax)
          },
          symbolOffset: [0, yOffsets[idx]]
        };
      });

      series.push({
        name: featureName, // Used for potential legend/tooltip filtering
        type: "scatter",
        symbolSize: 4,
        data: data,
        emphasis: {
          focus: "none"
        }
      });
    }

    return {
      animation: false,
      tooltip: {
        trigger: "item",
        formatter: function (params) {
          return (
            `Entity: ${params.data.value[2]}<br/>` +
            `Group: ${params.data.value[3]}<br/>` +
            `Feature: ${String(params.data.value[1])}<br/>` +
            `Feature value: ${params.data.value[4]}<br/>` +
            `SHAP value${targetUnitLabel}: ${params.data.value[0].toFixed(4)}<br/>`
          );
        }
      },
      grid: {
        top: 0,
        left: isFullscreen ? 158 : 108,
        right: 20,
        bottom: 45,
        containLabel: false
      },
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: 0
        },
        {
          type: "inside",
          yAxisIndex: 0
        }
      ],
      xAxis: {
        type: "value",
        name: `SHAP value${targetUnitLabel} (impact on model output)`,
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "category",
        data: categoryData,
        axisLabel: {
          interval: 0,
          fontSize: isFullscreen ? 12 : 10,
          rotate: 0,
          width: isFullscreen ? 150 : 100,
          overflow: "truncate",
          lineHeight: isFullscreen ? 12 : 9.5,
          formatter: function (value) {
            return formatAxisLabel({
              value,
              dataLength: categoryData.length,
              isFullscreen
            });
          }
        },
        axisPointer: {
          show: false
        }
      },
      visualMap: {
        calculable: false,
        orient: "vertical",
        right: 0,
        top: "center",
        dimension: 3,
        inRange: {
          color: [
            "rgb(0,0,255)", // blue
            "rgb(128,0,128)", // purple (middle)
            "rgb(255,0,0)" // red
          ]
        },
        formatter: function (value) {
          return (value as number).toFixed(2);
        },
        text: ["High", "Low"], // Labels for legend ends
        itemWidth: 15,
        itemHeight: 100
      },
      series: series
    };
  }
};
